import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { TrendingUp, Users } from 'lucide-react';

const API_BASE = import.meta.env.VITE_API_BASE_URL || '';

interface SignUpStats {
  employeeId: string;
  employeeName: string;
  position: string;
  totalSignUps: number;
  boxers: Array<{
    id: string;
    name: string;
    stateId: string;
    registeredDate: string;
  }>;
}

export function SignUpDashboard() {
  const [stats, setStats] = useState<SignUpStats[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [expandedEmployee, setExpandedEmployee] = useState<string | null>(null);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await fetch(`${API_BASE}/api/signup-stats`);
      
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      } else {
        setError('Failed to fetch sign-up statistics');
      }
    } catch (err) {
      setError('An error occurred while fetching statistics');
    } finally {
      setLoading(false);
    }
  };

  const toggleExpand = (employeeId: string) => {
    setExpandedEmployee(expandedEmployee === employeeId ? null : employeeId);
  };

  const totalBoxers = stats.reduce((sum, stat) => sum + stat.totalSignUps, 0);

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Employees</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.length}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Boxer Sign-Ups</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{totalBoxers}</div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Employee Performance</CardTitle>
        </CardHeader>
        <CardContent>
          {error && (
            <Alert variant="destructive" className="mb-4">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {loading ? (
            <p className="text-center text-gray-500">Loading statistics...</p>
          ) : stats.length === 0 ? (
            <p className="text-center text-gray-500">No data available</p>
          ) : (
            <div className="space-y-4">
              {stats.map((stat) => (
                <div
                  key={stat.employeeId}
                  className="border rounded-lg p-4 hover:bg-gray-50 transition-colors"
                >
                  <div
                    className="flex justify-between items-center cursor-pointer"
                    onClick={() => toggleExpand(stat.employeeId)}
                  >
                    <div>
                      <h3 className="font-semibold text-lg">{stat.employeeName}</h3>
                      <p className="text-sm text-gray-600">{stat.position}</p>
                    </div>
                    <div className="text-right">
                      <div className="text-2xl font-bold text-blue-600">
                        {stat.totalSignUps}
                      </div>
                      <p className="text-xs text-gray-500">Boxers Signed</p>
                    </div>
                  </div>

                  {expandedEmployee === stat.employeeId && stat.boxers.length > 0 && (
                    <div className="mt-4 pt-4 border-t">
                      <h4 className="font-medium mb-3">Signed-Up Boxers:</h4>
                      <div className="space-y-2">
                        {stat.boxers.map((boxer) => (
                          <div
                            key={boxer.id}
                            className="flex justify-between items-center bg-white p-3 rounded border"
                          >
                            <div>
                              <p className="font-medium">{boxer.name}</p>
                              <p className="text-sm text-gray-600">
                                State ID: {boxer.stateId}
                              </p>
                            </div>
                            <div className="text-sm text-gray-500">
                              {new Date(boxer.registeredDate).toLocaleDateString()}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
